/**
 * Bank Accounts — Settings / Billing
 *
 * List-and-form UI for CompanySettings → BankAccounts CRUD, using the shared
 * dynamic form modal (``utils/ui/dynamic-modal.js`` / ``form-modals.mdc``)
 * instead of a bespoke drawer.
 *
 * Markup contract
 * ---------------
 * - Root with `data-auto-init="bankAccounts"` and
 *   `data-list-url="/office/settings/billing/bank-accounts/"`.
 * - Inside the root: `#bank-accounts-list` (current list fragment) and the
 *   modal shell `#bank-account-modal` (`.modal-content` / `.modal-title` /
 *   `.modal-close-btn`).
 * - Open buttons: `[data-bank-account-open][data-url="…"]`.
 *
 * Server contract
 * ---------------
 * - GET on the form URL → the form partial (modal content).
 * - POST success → JSON `{success: true}`: the helper closes the modal, we
 *   re-fetch the list fragment and toast.
 * - POST failure → the form partial with bound errors (repainted inline).
 */

import { createDynamicModal } from '../utils/ui/dynamic-modal.js';
import { attachBicAutoDerive } from '../utils/forms/bic-autoderive.js';

function findClosest(el, selector) {
    // event.target can be a text node (no .closest); promote to its parent.
    var node = (el && el.nodeType === 1) ? el : (el && el.parentElement) || null;
    if (!node || typeof node.closest !== 'function') return null;
    return node.closest(selector);
}

function toast(message, type) {
    if (typeof window.showToast === 'function') {
        window.showToast(message, type || 'success');
    }
}

function wireBicAutoDerive(container) {
    // Live IBAN → BIC fill. The form is re-rendered from server HTML on every
    // open (and on every validation re-render), so we re-attach each time; the
    // helper itself guards against double-binding via a per-input flag.
    if (!container) return;
    var ibanInput = container.querySelector('input[name="iban"], input[name$="-iban"]');
    var bicInput = container.querySelector('input[name="bic"], input[name$="-bic"]');
    if (ibanInput && bicInput) {
        attachBicAutoDerive({ ibanInput: ibanInput, bicInput: bicInput });
    }
}

async function refreshList(root) {
    var listEl = document.getElementById('bank-accounts-list');
    var url = root ? root.dataset.listUrl : null;
    if (!listEl || !url) return;
    try {
        var resp = await fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin',
        });
        if (!resp.ok) return;
        listEl.innerHTML = await resp.text();
        // Re-init the row tooltips (Flowbite) on the freshly-swapped list.
        if (typeof window.reinitFlowbiteWidgets === 'function') {
            window.reinitFlowbiteWidgets(listEl);
        }
    } catch (err) {
        console.error('[bank-accounts] list refresh failed', err);
    }
}

export function initBankAccounts() {
    var root = document.querySelector('[data-auto-init="bankAccounts"]');
    if (!root) return;

    // Create the modal helper once per modal DOM node. The node lives inside
    // #main-content, so it is replaced on every boosted nav — a fresh node has
    // no cached helper and gets re-wired; the old node is GC'd with its
    // listeners. (hx-boost-init.mdc rule 3: per-element flag.)
    // i18n strings are surfaced via {% trans %}-rendered data-attrs on the
    // root (i18n-english-msgids.mdc rule 5); English is the msgid fallback.
    var savedMsg = root.dataset.savedMsg || 'Bank account saved.';
    var errorMsg = root.dataset.errorMsg || 'Error loading form. Please try again.';

    var modalEl = document.getElementById('bank-account-modal');
    if (modalEl && !modalEl._bankModalHelper) {
        modalEl._bankModalHelper = createDynamicModal({
            modalId: 'bank-account-modal',
            errorLoadingMessage: errorMsg,
            onContentInjected: function (content) {
                if (typeof window.initCustomSelects === 'function') {
                    window.initCustomSelects();
                }
                wireBicAutoDerive(content);
            },
            onSuccess: function (data, helper) {
                // Providing onSuccess opts out of the helper's default
                // close+reload, so we must close it ourselves.
                if (helper && typeof helper.close === 'function') helper.close();
                refreshList(root);
                toast(savedMsg, 'success');
            },
        });
    }

    // Delegated open trigger; survives list re-renders. Per-root flag so a
    // boosted re-init doesn't stack a second click handler on the same node.
    if (!root._bankOpenBound) {
        root._bankOpenBound = true;
        root.addEventListener('click', function (event) {
            var opener = findClosest(event.target, '[data-bank-account-open]');
            if (!opener) return;
            event.preventDefault();
            var url = opener.getAttribute('data-url');
            var helper = modalEl && modalEl._bankModalHelper;
            if (url && helper) helper.open(url);
        });
    }
}

export function autoInitBankAccounts() {
    initBankAccounts();
}
